import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for implementing pull-to-refresh functionality on mobile
 * @param {Object} options - Configuration options
 * @param {function} options.onRefresh - Callback function to execute on refresh
 * @param {number} [options.threshold=80] - Distance to pull before triggering refresh
 * @param {number} [options.resistance=2.5] - Resistance factor for pull distance
 * @param {boolean} [options.enabled=true] - Whether pull-to-refresh is enabled
 * @returns {Object} - Ref to attach to scrollable element and refresh state
 */
function usePullToRefresh({
  onRefresh,
  threshold = 80,
  resistance = 2.5,
  enabled = true
}) {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [canPull, setCanPull] = useState(false);
  
  const elementRef = useRef(null);
  const touchStartY = useRef(0);
  const touchCurrentY = useRef(0);
  const isAtTop = useRef(true);

  // Check if element is scrolled to top
  const checkScrollPosition = () => {
    const element = elementRef.current;
    if (!element) return false;
    
    const scrollTop = element.scrollTop || window.pageYOffset || document.documentElement.scrollTop;
    return scrollTop <= 0;
  };

  // Handle touch start
  const handleTouchStart = (e) => {
    if (!enabled || isRefreshing) return;
    
    isAtTop.current = checkScrollPosition();
    if (!isAtTop.current) return;
    
    touchStartY.current = e.touches[0].clientY;
    setCanPull(true);
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    if (!enabled || !canPull || isRefreshing) return;
    
    touchCurrentY.current = e.touches[0].clientY;
    const pullDistance = Math.max(0, (touchCurrentY.current - touchStartY.current) / resistance);
    
    if (pullDistance > 0) {
      setIsPulling(true);
      setPullDistance(pullDistance);
      
      // Prevent default scrolling when pulling
      if (pullDistance > 10) {
        e.preventDefault();
      }
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    if (!enabled || !canPull || isRefreshing) return;
    
    setCanPull(false);
    
    if (pullDistance >= threshold) {
      // Trigger refresh
      setIsRefreshing(true);
      
      const refreshPromise = onRefresh ? onRefresh() : Promise.resolve();
      
      // Ensure minimum refresh time for UX
      Promise.all([
        refreshPromise,
        new Promise(resolve => setTimeout(resolve, 1000))
      ]).finally(() => {
        setIsRefreshing(false);
        setIsPulling(false);
        setPullDistance(0);
      });
    } else {
      // Reset pull state
      setIsPulling(false);
      setPullDistance(0);
    }
  };

  // Attach event listeners
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;

    // Add passive event listeners for better performance
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Also listen on window for cases where touch moves outside element
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, canPull, pullDistance, threshold, isRefreshing]);

  // Calculate pull progress (0-1)
  const pullProgress = Math.min(pullDistance / threshold, 1);
  
  // Determine pull state
  const pullState = isRefreshing ? 'refreshing' : 
                   pullDistance >= threshold ? 'ready' : 
                   isPulling ? 'pulling' : 'idle';

  return {
    ref: elementRef,
    isPulling,
    isRefreshing,
    pullDistance,
    pullProgress,
    pullState,
    canRefresh: pullDistance >= threshold
  };
}

export default usePullToRefresh;